import React, {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Building2, Gavel, Loader2, ShieldAlert, User } from "lucide-react";
import { toast } from "react-toastify";

import { AmlPanel } from "./aml/AmlPanel";
import { LegalHistoryDrawer } from "./LegalHistoryDrawer";
import { ScreeningCountCell } from "./aml/ScreeningCountCell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { normalizeScreening } from "../lib/aml/normalize";
import type { AmlBucket, AmlListRow, AmlScreeningResult } from "../lib/aml/types";
import { fetchScoremeReference, getAmlList, getAmlView } from "../services/amlApi";
import { H2 } from "../pages/customer-details/components/UI";

const eligibleLegalRows = (rows: AmlListRow[]) =>
  rows.filter(
    (row) => row.director_type != null && String(row.director_type).trim() !== ""
  );

const hasPendingLegalFetch = (rows: AmlListRow[]) =>
  eligibleLegalRows(rows).some(
    (row) => String(row.is_legal_fetch ?? "").trim().toLowerCase() === "no"
  );

interface LegalCase {
  petitioner: string;
  respondent: string;
  court: string;
  date: string;
  case_status: string;
  case_number: string;
  case_type: string;
  case_category: string;
  severity: string;
}

interface LegalTabProps {
  legalCases?: LegalCase[];
  customerId?: string;
}

type CaseDirection = "against" | "by";

const EMPTY_LEGAL_CASES: LegalCase[] = [];

const AML_COLUMNS: { key: AmlBucket; label: string; countKey: keyof AmlListRow }[] = [
  {
    key: "pep",
    label: "Politically Exposed Person",
    countKey: "politically_exposed_person_count",
  },
  {
    key: "sanctions",
    label: "Global Sanctions",
    countKey: "global_sanctions_count",
  },
  {
    key: "amlPenalties",
    label: "AML Penalties",
    countKey: "aml_penalties_count",
  },
];

const compareCases = (a: LegalCase, b: LegalCase) => {
  if (a.case_status !== b.case_status) {
    return a.case_status === "Pending" ? -1 : 1;
  }
  return new Date(b.date).getTime() - new Date(a.date).getTime();
};

const Kpi = memo(function Kpi({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: typeof Gavel;
  tone: string;
}) {
  return (
    <Card className="gap-2 p-4">
      <span className={`flex size-9 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="size-4" />
      </span>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </Card>
  );
});

const severityBadgeClass = (severity: string) => {
  const normalized = severity?.toLowerCase() ?? "";
  if (normalized === "high") return "bg-red-100 text-red-700 border-transparent";
  if (normalized === "medium") return "bg-orange-100 text-orange-700 border-transparent";
  return "bg-green-100 text-green-700 border-transparent";
};

const statusBadgeClass = (status: string) =>
  status === "Pending" ? "text-orange-600 border-orange-200" : "text-green-700 border-green-200";

const LegalCaseRow = memo(function LegalCaseRow({
  legalCase,
  selectedDirection,
}: {
  legalCase: LegalCase;
  selectedDirection: CaseDirection;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">{legalCase.case_category}</TableCell>
      <TableCell className="text-muted-foreground">{legalCase.court}</TableCell>
      <TableCell className="max-w-72 whitespace-normal">
        {selectedDirection === "against" ? legalCase.petitioner : legalCase.respondent}
      </TableCell>
      <TableCell className="font-medium">{legalCase.case_number}</TableCell>
      <TableCell>
        <Badge
          variant="secondary"
          className={`capitalize ${severityBadgeClass(legalCase.severity)}`}
        >
          {legalCase.severity}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={statusBadgeClass(legalCase.case_status)}>
          {legalCase.case_status}
        </Badge>
      </TableCell>
    </TableRow>
  );
});

const AmlScreeningRow = memo(function AmlScreeningRow({
  row,
  viewLoadingId,
  panelBucket,
  onOpenAml,
  onOpenLegalHistory,
}: {
  row: AmlListRow;
  viewLoadingId: string | null;
  panelBucket?: AmlBucket;
  onOpenAml: (row: AmlListRow, bucket?: AmlBucket) => void;
  onOpenLegalHistory: (row: AmlListRow) => void;
}) {
  const isCompany = !row.director_full_name;
  const displayName = isCompany
    ? row.entity_name
    : row.director_full_name || row.entity_name;
  const role = isCompany ? null : row.director_type;
  const Icon = isCompany ? Building2 : User;
  const rowId = String(row.id);
  const legalCasesRaw = row.total_legal_cases;
  const legalCasesValue =
    legalCasesRaw === null || legalCasesRaw === undefined || legalCasesRaw === ""
      ? null
      : Number(legalCasesRaw);
  const canOpenLegalHistory = !isCompany && (legalCasesValue ?? 0) > 0;

  return (
    <TableRow id={`entity-${rowId}`}>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="flex size-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Icon className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium">{displayName}</p>
            {role && <p className="truncate text-xs text-muted-foreground">{role}</p>}
          </div>
        </div>
      </TableCell>
      {AML_COLUMNS.map(({ key, label, countKey }) => {
        const count = Number(row[countKey] ?? 0);
        return (
          <TableCell key={key} className="border-l">
            <ScreeningCountCell
              count={count}
              loading={viewLoadingId === rowId && panelBucket === key}
              onOpen={() => onOpenAml(row, key)}
            />
            <span className="sr-only">{label}</span>
          </TableCell>
        );
      })}
      <TableCell className="border-l">
        {legalCasesValue === null || Number.isNaN(legalCasesValue) ? (
          <span className="text-sm text-muted-foreground">-</span>
        ) : legalCasesValue > 0 ? (
          canOpenLegalHistory ? (
            <button
              type="button"
              onClick={() => onOpenLegalHistory(row)}
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Badge
                variant="secondary"
                className="cursor-pointer bg-red-100 text-red-700 hover:bg-red-200"
              >
                {legalCasesValue} {legalCasesValue === 1 ? "Case" : "Cases"}
              </Badge>
            </button>
          ) : (
            <Badge variant="secondary" className="bg-red-100 text-red-700 hover:bg-red-100">
              {legalCasesValue} {legalCasesValue === 1 ? "Case" : "Cases"}
            </Badge>
          )
        ) : (
          <span className="text-sm tabular-nums">{legalCasesValue}</span>
        )}
      </TableCell>
    </TableRow>
  );
});

const LegalTab: React.FC<LegalTabProps> = ({
  legalCases = EMPTY_LEGAL_CASES,
  customerId,
}) => {
  const [selectedDirection, setSelectedDirection] = useState<CaseDirection>("against");
  const [openSections, setOpenSections] = useState<string[]>(["against-Pending"]);
  const [amlRows, setAmlRows] = useState<AmlListRow[]>([]);
  const [amlLoading, setAmlLoading] = useState(false);
  const [amlError, setAmlError] = useState<string | null>(null);
  const [viewCache, setViewCache] = useState<Record<string, AmlScreeningResult>>({});
  const [viewLoadingId, setViewLoadingId] = useState<string | null>(null);
  const [panel, setPanel] = useState<{
    rowId: string;
    name: string;
    bucket?: AmlBucket;
  } | null>(null);
  const [bulkFetchOpen, setBulkFetchOpen] = useState(false);
  const [fetchingLegal, setFetchingLegal] = useState(false);
  const [legalHistory, setLegalHistory] = useState<{
    directorUuid: string;
    directorName: string;
    totalLegalCases: number;
  } | null>(null);

  const viewCacheRef = useRef(viewCache);
  viewCacheRef.current = viewCache;

  const canBulkFetchLegal = !fetchingLegal && hasPendingLegalFetch(amlRows);

  const loadAmlList = useCallback(async (opts?: { silent?: boolean }) => {
    if (!customerId) return [] as AmlListRow[];
    if (!opts?.silent) {
      setAmlLoading(true);
      setAmlError(null);
    }
    try {
      const rows = await getAmlList(customerId);
      setAmlRows(rows);
      return rows;
    } catch (err) {
      if (!opts?.silent) {
        setAmlError(
          err instanceof Error
            ? err.message
            : typeof err === "object" && err && "message" in err
              ? String((err as { message: unknown }).message)
              : "Failed to load AML screening"
        );
        setAmlRows([]);
      }
      return [] as AmlListRow[];
    } finally {
      if (!opts?.silent) setAmlLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    void loadAmlList();
  }, [customerId, loadAmlList]);

  const handleBulkFetchLegal = useCallback(async () => {
    if (!customerId) {
      toast.error("Customer ID is missing");
      return;
    }
    setFetchingLegal(true);
    setBulkFetchOpen(false);
    try {
      await fetchScoremeReference({ customer_id: customerId, director_uuid: "" });
      toast.success("Legal verification initiated for all directors");

      // Poll until every eligible director has a response (is_legal_fetch !== "No"), or timeout
      const maxAttempts = 15;
      const intervalMs = 2000;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const rows = await loadAmlList({ silent: true });
        if (!hasPendingLegalFetch(rows)) {
          toast.success("Legal data fetched for all directors");
          return;
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }
      // Some directors still pending — Fetch Legal button will show again
      toast.info("Some directors are still pending. You can fetch again.");
    } catch (err) {
      console.error("Failed to fetch legal:", err);
      toast.error("Failed to initiate legal verification");
      await loadAmlList({ silent: true });
    } finally {
      setFetchingLegal(false);
    }
  }, [customerId, loadAmlList]);

  const openLegalHistory = useCallback((row: AmlListRow) => {
    const raw = row as AmlListRow & Record<string, unknown>;
    const directorUuid = String(
      raw.director_uuid ?? raw.directors_uuid ?? raw.uuid ?? ""
    ).trim();
    if (!directorUuid) {
      toast.error("Director UUID is missing");
      return;
    }
    setLegalHistory({
      directorUuid,
      directorName: row.director_full_name || row.entity_name || "Director",
      totalLegalCases: Number(row.total_legal_cases) || 0,
    });
  }, []);

  const openAmlPanel = useCallback(async (row: AmlListRow, bucket?: AmlBucket) => {
    const rowId = String(row.id);
    const name = row.director_full_name || row.entity_name || "Entity";
    setPanel({ rowId, name, bucket });

    if (viewCacheRef.current[rowId]) return;

    setViewLoadingId(rowId);
    try {
      const raw = await getAmlView(row.id);
      const result = normalizeScreening(raw, name);
      setViewCache((prev) => ({ ...prev, [rowId]: result }));
    } catch (err) {
      console.error("Failed to load AML view:", err);
      setViewCache((prev) => ({
        ...prev,
        [rowId]: {
          status: "ERROR",
          total: 0,
          searchId: null,
          screenedName: name,
          screenedAt: new Date().toISOString(),
          matches: [],
          exactCount: 0,
          fuzzyCount: 0,
          sources: [],
        },
      }));
    } finally {
      setViewLoadingId(null);
    }
  }, []);

  const handleDirectionChange = useCallback((direction: CaseDirection) => {
    startTransition(() => {
      setSelectedDirection(direction);
      setOpenSections([`${direction}-Pending`]);
    });
  }, []);

  const casesByType = useMemo(() => {
    const againstCompany: LegalCase[] = [];
    const byCompany: LegalCase[] = [];

    for (const cs of legalCases) {
      if (cs.case_type === "Cases Filed Against This Corporate") {
        againstCompany.push(cs);
      } else if (cs.case_type === "Cases Filed By This Corporate") {
        byCompany.push(cs);
      }
    }

    againstCompany.sort(compareCases);
    byCompany.sort(compareCases);

    return { againstCompany, byCompany };
  }, [legalCases]);

  const summary = useMemo(() => {
    let againstCompanyPending = 0;
    let againstCompanyDisposed = 0;
    let byCompanyPending = 0;
    let byCompanyDisposed = 0;

    for (const c of casesByType.againstCompany) {
      if (c.case_status === "Pending") againstCompanyPending += 1;
      else if (c.case_status === "Disposed") againstCompanyDisposed += 1;
    }
    for (const c of casesByType.byCompany) {
      if (c.case_status === "Pending") byCompanyPending += 1;
      else if (c.case_status === "Disposed") byCompanyDisposed += 1;
    }

    return {
      againstCompanyPending,
      againstCompanyDisposed,
      byCompanyPending,
      byCompanyDisposed,
    };
  }, [casesByType]);

  const casesByStatus = useMemo(() => {
    const source =
      selectedDirection === "against" ? casesByType.againstCompany : casesByType.byCompany;
    const pending: LegalCase[] = [];
    const disposed: LegalCase[] = [];

    for (const row of source) {
      if (row.case_status === "Pending") pending.push(row);
      else if (row.case_status === "Disposed") disposed.push(row);
    }

    return { Pending: pending, Disposed: disposed };
  }, [casesByType, selectedDirection]);

  const pendingCount =
    selectedDirection === "against" ? summary.againstCompanyPending : summary.byCompanyPending;
  const disposedCount =
    selectedDirection === "against" ? summary.againstCompanyDisposed : summary.byCompanyDisposed;
  const partyColumnLabel = selectedDirection === "against" ? "Filed By" : "Filed Against";

  const statusGroups = useMemo(
    () =>
      [
        { status: "Pending" as const, label: "Pending Cases", count: pendingCount },
        { status: "Disposed" as const, label: "Disposed Cases", count: disposedCount },
      ] as const,
    [pendingCount, disposedCount]
  );

  const hasLegalCases = legalCases.length > 0;
  const panelResult = panel ? viewCache[panel.rowId] ?? null : null;
  const panelLoading = Boolean(panel && viewLoadingId === panel.rowId);

  return (
    <div className="space-y-6">
      {hasLegalCases ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi
            label="Pending Cases Against Company"
            value={summary.againstCompanyPending}
            icon={Building2}
            tone="bg-red-100 text-red-700"
          />
          <Kpi
            label="Disposed Cases Against Company"
            value={summary.againstCompanyDisposed}
            icon={Building2}
            tone="bg-green-100 text-green-700"
          />
          <Kpi
            label="Pending Cases Filed by Company"
            value={summary.byCompanyPending}
            icon={Gavel}
            tone="bg-orange-100 text-orange-700"
          />
          <Kpi
            label="Disposed Cases Filed by Company"
            value={summary.byCompanyDisposed}
            icon={Gavel}
            tone="bg-green-100 text-green-700"
          />
        </div>
      ) : (
        <>
          <H2 className="">Legal</H2>
          <div className="rounded-lg border border-dashed p-6 text-center text-gray-500">
            No legal cases found against or filed by this company.
          </div>
        </>
      )}

      <Card className="gap-0 overflow-hidden p-0" id="screening">
        <div className="border-b bg-muted/40 px-4 py-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="size-4 text-muted-foreground" />
            <p className="text-sm font-semibold">AML Screening</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Review company and director matches across sanctions and AML enforcement sources.
          </p>
        </div>
        <div className="overflow-x-auto">
          {amlLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading AML screening…
            </div>
          ) : amlError ? (
            <div className="px-4 py-8 text-center text-sm text-red-600">{amlError}</div>
          ) : amlRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No AML screening data available.
            </div>
          ) : (
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <div className="flex items-center gap-3">
                      <span>Name</span>
                      {customerId && fetchingLegal && (
                        <span className="inline-flex h-7 items-center gap-1.5 rounded-md bg-purple-100 px-2.5 text-xs font-medium text-purple-800">
                          <Loader2 className="size-3.5 animate-spin" />
                          Fetching legal…
                        </span>
                      )}
                      {customerId && canBulkFetchLegal && (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="h-7 gap-1.5 bg-purple-100 text-purple-800 hover:bg-purple-200"
                          onClick={() => setBulkFetchOpen(true)}
                        >
                          <Gavel className="size-3.5" />
                          Fetch Legal
                        </Button>
                      )}
                    </div>
                  </TableHead>
                  {AML_COLUMNS.map((column) => (
                    <TableHead key={column.key} className="border-l">
                      {column.label}
                    </TableHead>
                  ))}
                  <TableHead className="border-l">Total Legal Cases</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {amlRows.map((row) => (
                  <AmlScreeningRow
                    key={String(row.id)}
                    row={row}
                    viewLoadingId={viewLoadingId}
                    panelBucket={panel?.bucket}
                    onOpenAml={openAmlPanel}
                    onOpenLegalHistory={openLegalHistory}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      {hasLegalCases && (
        <Card className="gap-0 overflow-hidden p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/40 px-4 py-3">
            <div className="flex items-center gap-2">
              <Gavel className="size-4 text-muted-foreground" />
              <p className="text-sm font-semibold">Legal Cases on Company</p>
            </div>
            <div
              className="flex items-center rounded-md border bg-background p-1"
              aria-label="Legal cases case direction"
            >
              <Button
                type="button"
                size="sm"
                variant={selectedDirection === "against" ? "default" : "ghost"}
                className="h-7"
                onClick={() => handleDirectionChange("against")}
              >
                Filed Against
              </Button>
              <Button
                type="button"
                size="sm"
                variant={selectedDirection === "by" ? "default" : "ghost"}
                className="h-7"
                onClick={() => handleDirectionChange("by")}
              >
                Filed By
              </Button>
            </div>
          </div>

          <Accordion type="multiple" value={openSections} onValueChange={setOpenSections}>
            {statusGroups.map(({ status, label, count }) => {
              const sectionValue = `${selectedDirection}-${status}`;
              const isOpen = openSections.includes(sectionValue);
              const cases = casesByStatus[status];

              return (
                <AccordionItem
                  key={status}
                  value={sectionValue}
                  className="px-4 last:border-b-0"
                >
                  <AccordionTrigger className="py-4 hover:no-underline">
                    <span className="flex flex-1 items-center gap-3 text-left">
                      <span className="font-semibold">{label}</span>
                      <Badge
                        variant="secondary"
                        className={
                          status === "Pending"
                            ? "bg-red-100 text-red-700 hover:bg-red-100"
                            : "bg-purple-100 text-purple-800 hover:bg-purple-100"
                        }
                      >
                        {count} {count === 1 ? "Match" : "Matches"}
                      </Badge>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    {!isOpen ? null : cases.length === 0 ? (
                      <p className="border-t py-6 text-sm text-muted-foreground">
                        No legal cases available
                      </p>
                    ) : (
                      <div className="overflow-x-auto border-t">
                        <Table className="min-w-[920px]">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Case Category</TableHead>
                              <TableHead>Court</TableHead>
                              <TableHead>{partyColumnLabel}</TableHead>
                              <TableHead>Case No.</TableHead>
                              <TableHead>Severity</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {cases.map((legalCase, index) => (
                              <LegalCaseRow
                                key={`${legalCase.case_number}-${legalCase.case_status}-${index}`}
                                legalCase={legalCase}
                                selectedDirection={selectedDirection}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </Card>
      )}

      {panel && (
        <AmlPanel
          open
          onOpenChange={(next) => !next && setPanel(null)}
          directorName={panel.name}
          result={panelResult}
          loading={panelLoading}
          bucket={panel.bucket}
        />
      )}

      {legalHistory && (
        <LegalHistoryDrawer
          open
          onOpenChange={(next) => !next && setLegalHistory(null)}
          directorName={legalHistory.directorName}
          directorUuid={legalHistory.directorUuid}
          totalLegalCases={legalHistory.totalLegalCases}
        />
      )}

      <AlertDialog open={bulkFetchOpen} onOpenChange={setBulkFetchOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Fetch Legal</AlertDialogTitle>
            <AlertDialogDescription>
              This will initiate legal verification for all directors under this customer. Do you
              want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={fetchingLegal}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={fetchingLegal}
              onClick={(e) => {
                e.preventDefault();
                void handleBulkFetchLegal();
              }}
            >
              {fetchingLegal ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Fetching…
                </>
              ) : (
                "Confirm"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default memo(LegalTab);
